import { Injectable, inject } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import type { CompletedShipmentConfirmation } from '../api/shipment/models/completed-shipment.model';
import { isValidCompletedShipment } from '../api/shipment/models/completed-shipment.model';
import type { ShipmentAddressesFormValue } from '../../pages/ship-from-ship-to/models/address-form.model';
import type { PackageSelectionFormValue } from '../../pages/package-selection/forms/package-selection.form';
import type { AdditionalCoverageSavePayload } from '../../pages/service-step/forms/additional-coverage.form';
import type { SignatureProtectionSavePayload } from '../../pages/service-step/forms/signature-protection.form';
import type { SelectedDeliveryService } from '../../pages/service-step/sections/delivery-service-selection/delivery-service-selection.model';
import type { PickupDropOffSavePayload } from '../../pages/service-step/sections/pickup-drop-off/pickup-drop-off.state';

/** Shipment wizard step identifiers for persisted flow state. */
export const SHIPMENT_WIZARD_STEPS = {
  addresses: 'addresses',
  package: 'package',
  service: 'service',
  reviewAndPay: 'reviewAndPay',
  getLabels: 'getLabels',
} as const;

export type ShipmentWizardStep = (typeof SHIPMENT_WIZARD_STEPS)[keyof typeof SHIPMENT_WIZARD_STEPS];

/** Combined Service step payload (pickup/drop-off + coverage + signature). */
export interface ServiceStepSavePayload {
  pickupDropOff: PickupDropOffSavePayload;
  additionalCoverage: AdditionalCoverageSavePayload;
  signatureProtection: SignatureProtectionSavePayload;
}

/** Opaque JWT kinds stored as a single string token. */
export type ShipmentFlowTokenAttestationKind =
  | 'addressValidation'
  | 'serviceAbility'
  | 'serviceAvailability'
  | 'packageSelection'
  | 'account'
  | 'estimate'
  | 'pickupValidation';

/** Wizard JWT tokens captured from successful Lambda responses. */
export interface ShipmentFlowAttestations {
  addressValidation?: string;
  serviceAbility?: string;
  /** Product Service Availability JWT_psa minted on Package step load (OM-872). */
  serviceAvailability?: string;
  packageSelection?: string;
  /** Account Lookup JWT_acct minted on Package Save and Continue. */
  account?: string;
  /** Product-selection JWTs keyed by product code (Service step catalog). */
  productSelectionByProductCode?: Record<string, string>;
  /** Single Estimates Manager JWT_est from POST /estimates/estimate. */
  estimate?: string;
  /** Final selected-window JWT_pickup bound to the active flow. */
  pickupValidation?: string;
}

/** Optional coupon applied during estimates orchestration. */
export interface ShipmentFlowCoupon {
  couponCode: string;
  email: string;
}

/** Persisted shipment flow state for back/forward navigation. */
export interface ShipmentFlowState {
  flowId: string;
  addresses?: ShipmentAddressesFormValue;
  package?: PackageSelectionFormValue;
  pickupDropOff?: PickupDropOffSavePayload;
  additionalCoverage?: AdditionalCoverageSavePayload;
  signatureProtection?: SignatureProtectionSavePayload;
  selectedService?: SelectedDeliveryService;
  /** Completed-shipment confirmation for Get Labels (OM-66); cleared with the flow. */
  completedShipment?: CompletedShipmentConfirmation;
  completedSteps: ShipmentWizardStep[];
  attestations?: ShipmentFlowAttestations;
  coupon?: ShipmentFlowCoupon;
  /**
   * Destination residential flag from service-ability (ESD), used for signature defaults.
   * Not derived from the service-ability JWT.
   */
  destinationResidential?: boolean;
  /**
   * Okta subject that owns this tab's wizard snapshot.
   * Used after session expiry so a different user cannot inherit the previous shipment.
   */
  ownerSubject?: string;
}

const STORAGE_KEY = 'dsc.eso.shipment-flow';

/**
 * Persists create-shipment wizard state in sessionStorage across step navigation.
 */
@Injectable({ providedIn: 'root' })
export class ShipmentFlowStateService {
  private readonly auth = inject(AuthService, { optional: true });

  /**
   * Returns an existing flow id or mints and persists a new one for this wizard session.
   */
  ensureFlowId(): string {
    const existing = this.load();
    if (existing?.flowId) {
      return existing.flowId;
    }

    const flowId = crypto.randomUUID();
    this.persist({
      flowId,
      completedSteps: existing?.completedSteps ?? [],
      addresses: existing?.addresses,
      package: existing?.package,
      pickupDropOff: existing?.pickupDropOff,
      additionalCoverage: existing?.additionalCoverage,
      signatureProtection: existing?.signatureProtection,
      selectedService: existing?.selectedService,
      completedShipment: existing?.completedShipment,
      attestations: existing?.attestations,
      coupon: existing?.coupon,
      destinationResidential: existing?.destinationResidential,
    });
    return flowId;
  }

  /**
   * Saves the address step form snapshot and marks the addresses wizard step complete.
   * Clears a prior service selection and signature because origin/destination changes invalidate rates.
   */
  saveAddressStep(addresses: ShipmentAddressesFormValue): void {
    const existing = this.load();
    const completedSteps = new Set(existing?.completedSteps ?? []);
    completedSteps.add(SHIPMENT_WIZARD_STEPS.addresses);
    completedSteps.delete(SHIPMENT_WIZARD_STEPS.service);
    const flowId = existing?.flowId ?? crypto.randomUUID();

    this.persist({
      flowId,
      addresses,
      package: existing?.package,
      pickupDropOff: existing?.pickupDropOff,
      additionalCoverage: existing?.additionalCoverage,
      signatureProtection: undefined,
      selectedService: undefined,
      completedShipment: existing?.completedShipment,
      completedSteps: [...completedSteps],
      attestations: existing?.attestations,
      coupon: existing?.coupon,
      destinationResidential: existing?.destinationResidential,
    });
  }

  /**
   * Saves the Package step form snapshot and marks the package step complete.
   * Requires addresses to already be persisted; no-ops when flow state is missing.
   * Does not navigate — callers own Save → next-step wiring.
   * Clears a prior service selection and signature when package details change.
   */
  savePackageSelection(packageValue: PackageSelectionFormValue): void {
    const existing = this.load();
    if (!existing?.addresses) {
      return;
    }

    const completedSteps = new Set(existing.completedSteps ?? []);
    completedSteps.add(SHIPMENT_WIZARD_STEPS.package);
    completedSteps.delete(SHIPMENT_WIZARD_STEPS.service);

    this.persist({
      flowId: existing.flowId ?? crypto.randomUUID(),
      addresses: existing.addresses,
      package: packageValue,
      pickupDropOff: existing.pickupDropOff,
      additionalCoverage: existing.additionalCoverage,
      signatureProtection: undefined,
      selectedService: undefined,
      completedShipment: existing.completedShipment,
      completedSteps: [...completedSteps],
      attestations: existing.attestations,
      coupon: existing.coupon,
      destinationResidential: existing.destinationResidential,
    });
  }

  /**
   * Saves the Service step's pickup/drop-off payload and marks the service step complete.
   * Requires the package step to already be persisted; no-ops when flow state is missing
   * or the package step has not been completed.
   * @deprecated Prefer {@link saveServiceStep} which also persists additional coverage.
   */
  savePickupDropOff(payload: PickupDropOffSavePayload): void {
    const existing = this.load();
    this.saveServiceStep({
      pickupDropOff: payload,
      additionalCoverage: existing?.additionalCoverage ?? {
        declaredValue: null,
        packageDescription: null,
      },
      signatureProtection: existing?.signatureProtection ?? {
        requirement: 'none',
      },
    });
  }

  /**
   * Saves the Service step payloads (pickup/drop-off, coverage, signature) and marks
   * the service step complete. Requires the package step to already be persisted.
   */
  saveServiceStep(payload: ServiceStepSavePayload): void {
    const existing = this.load();
    if (!existing?.completedSteps.includes(SHIPMENT_WIZARD_STEPS.package)) {
      return;
    }

    const completedSteps = new Set(existing.completedSteps);
    completedSteps.add(SHIPMENT_WIZARD_STEPS.service);

    this.persist({
      flowId: existing.flowId ?? crypto.randomUUID(),
      addresses: existing.addresses,
      package: existing.package,
      pickupDropOff: payload.pickupDropOff,
      additionalCoverage: payload.additionalCoverage,
      signatureProtection: payload.signatureProtection,
      selectedService: existing.selectedService,
      completedShipment: existing.completedShipment,
      completedSteps: [...completedSteps],
      attestations: existing.attestations,
      coupon: existing.coupon,
      destinationResidential: existing.destinationResidential,
    });
  }

  /**
   * Stores optional coupon details so the Service step can send them to the estimates orchestrator.
   * Callers typically persist a coupon session from query params (`couponCode` + `couponEmail`).
   */
  saveCoupon(coupon: ShipmentFlowCoupon): void {
    const existing = this.load();
    const flowId = existing?.flowId ?? crypto.randomUUID();

    this.persist({
      flowId,
      addresses: existing?.addresses,
      package: existing?.package,
      pickupDropOff: existing?.pickupDropOff,
      additionalCoverage: existing?.additionalCoverage,
      signatureProtection: existing?.signatureProtection,
      selectedService: existing?.selectedService,
      completedSteps: existing?.completedSteps ?? [],
      attestations: existing?.attestations,
      coupon,
      destinationResidential: existing?.destinationResidential,
    });
  }

  /**
   * Persists completed-shipment confirmation for Get Labels and marks review + getLabels complete.
   * Used after successful payment/orchestration (or fixture seeding until that API exists).
   */
  saveCompletedShipment(payload: CompletedShipmentConfirmation): void {
    if (!isValidCompletedShipment(payload)) {
      return;
    }

    const existing = this.load();
    const completedSteps = new Set(existing?.completedSteps ?? []);
    completedSteps.add(SHIPMENT_WIZARD_STEPS.reviewAndPay);
    completedSteps.add(SHIPMENT_WIZARD_STEPS.getLabels);

    this.persist({
      flowId: existing?.flowId ?? crypto.randomUUID(),
      addresses: existing?.addresses,
      package: existing?.package,
      pickupDropOff: existing?.pickupDropOff,
      additionalCoverage: existing?.additionalCoverage,
      signatureProtection: existing?.signatureProtection,
      selectedService: existing?.selectedService,
      completedShipment: payload,
      completedSteps: [...completedSteps],
      attestations: existing?.attestations,
      coupon: existing?.coupon,
      destinationResidential: existing?.destinationResidential,
    });
  }

  /**
   * Removes a previously saved coupon so later Service-step estimates do not re-apply it.
   */
  clearCoupon(): void {
    const existing = this.load();
    if (!existing) {
      return;
    }

    this.persist({
      ...existing,
      coupon: undefined,
    });
  }

  /**
   * Returns true when session has a completed shipment with a tracking PIN (active Get Labels context).
   */
  hasActiveCompletedShipment(): boolean {
    return isValidCompletedShipment(this.load()?.completedShipment);
  }

  /**
   * After Okta re-login, reconciles this tab's wizard with the signed-in user.
   * Same user with a completed shipment returns to Get Labels; unfinished same-user
   * or any other identity clears the flow and starts at home (Ship From).
   *
   * @param subject Okta `sub` for the user who just signed in, or null when identity is unavailable
   * @returns A route to navigate to, or null to keep Okta's original URI
   */
  reconcileAfterLogin(subject: string | null): string | null {
    const stored = this.load();
    if (!stored) {
      return null;
    }

    if (!subject?.trim()) {
      return null;
    }

    if (!stored.ownerSubject || stored.ownerSubject !== subject) {
      this.clear();
      return '/';
    }

    if (this.hasActiveCompletedShipment()) {
      return '/get-labels';
    }

    this.clear();
    return '/';
  }

  /**
   * Stores one opaque JWT attestation token for later server-side shipment verify.
   * The browser does not decode or verify the token.
   */
  saveAttestation(kind: ShipmentFlowTokenAttestationKind, token: string): void {
    const existing = this.load();
    const flowId = existing?.flowId ?? crypto.randomUUID();

    this.persist({
      flowId,
      addresses: existing?.addresses,
      package: existing?.package,
      pickupDropOff: existing?.pickupDropOff,
      additionalCoverage: existing?.additionalCoverage,
      signatureProtection: existing?.signatureProtection,
      selectedService: existing?.selectedService,
      completedShipment: existing?.completedShipment,
      completedSteps: existing?.completedSteps ?? [],
      attestations: {
        ...existing?.attestations,
        [kind]: token,
      },
      coupon: existing?.coupon,
      destinationResidential: existing?.destinationResidential,
    });
  }

  /**
   * Persists the destination residential classification from service-ability JSON (not the JWT).
   */
  saveDestinationResidential(residential: boolean): void {
    const existing = this.load();
    const flowId = existing?.flowId ?? crypto.randomUUID();

    this.persist({
      flowId,
      addresses: existing?.addresses,
      package: existing?.package,
      pickupDropOff: existing?.pickupDropOff,
      additionalCoverage: existing?.additionalCoverage,
      signatureProtection: existing?.signatureProtection,
      selectedService: existing?.selectedService,
      completedShipment: existing?.completedShipment,
      completedSteps: existing?.completedSteps ?? [],
      attestations: existing?.attestations,
      coupon: existing?.coupon,
      destinationResidential: residential,
    });
  }

  /**
   * Stores product-selection JWTs keyed by product code after a successful catalog load.
   * Replaces any previously saved product-selection map for this wizard session.
   */
  saveProductSelectionAttestations(tokens: Record<string, string>): void {
    const existing = this.load();
    const flowId = existing?.flowId ?? crypto.randomUUID();

    this.persist({
      flowId,
      addresses: existing?.addresses,
      package: existing?.package,
      pickupDropOff: existing?.pickupDropOff,
      additionalCoverage: existing?.additionalCoverage,
      signatureProtection: existing?.signatureProtection,
      selectedService: existing?.selectedService,
      completedShipment: existing?.completedShipment,
      completedSteps: existing?.completedSteps ?? [],
      attestations: {
        ...existing?.attestations,
        productSelectionByProductCode: { ...tokens },
      },
      coupon: existing?.coupon,
      destinationResidential: existing?.destinationResidential,
    });
  }

  /**
   * Stores the single Estimates Manager JWT_est after a successful orchestrate response.
   */
  saveEstimateAttestation(token: string): void {
    this.saveAttestation('estimate', token);
  }

  /**
   * Removes one attestation token while keeping other wizard JWTs.
   * Used when Package Save must fail closed so a prior packageSelection JWT cannot travel with a different package type.
   */
  clearAttestation(kind: keyof ShipmentFlowAttestations): void {
    const existing = this.load();
    if (!existing?.attestations) {
      return;
    }

    const attestations = { ...existing.attestations };
    delete attestations[kind];
    this.persist({
      ...existing,
      attestations: Object.keys(attestations).length > 0 ? attestations : undefined,
    });
  }

  /**
   * Clears address-step attestation tokens so re-validation mints fresh JWTs.
   */
  clearAttestations(): void {
    const existing = this.load();
    if (!existing) {
      return;
    }

    this.persist({
      ...existing,
      attestations: undefined,
      destinationResidential: undefined,
    });
  }

  /**
   * Persists the current induction method and pickup draft without marking Service complete.
   * Used when the customer toggles methods or navigates Back before Save and Continue.
   */
  persistPickupDropOffProgress(payload: PickupDropOffSavePayload): void {
    const existing = this.load();
    if (!existing?.completedSteps.includes(SHIPMENT_WIZARD_STEPS.package)) {
      return;
    }

    this.persist({
      flowId: existing.flowId ?? crypto.randomUUID(),
      addresses: existing.addresses,
      package: existing.package,
      pickupDropOff: payload,
      additionalCoverage: existing.additionalCoverage,
      signatureProtection: existing.signatureProtection,
      selectedService: existing.selectedService,
      completedShipment: existing.completedShipment,
      completedSteps: existing.completedSteps,
      attestations: existing.attestations,
      coupon: existing.coupon,
      destinationResidential: existing.destinationResidential,
    });
  }

  /**
   * Persists the selected delivery service offering without requiring pickup/drop-off completion.
   * Requires the package step to already be persisted; no-ops when flow state is missing.
   */
  saveSelectedService(selection: SelectedDeliveryService): void {
    const existing = this.load();
    if (!existing?.completedSteps.includes(SHIPMENT_WIZARD_STEPS.package)) {
      return;
    }

    this.persist({
      flowId: existing.flowId ?? crypto.randomUUID(),
      addresses: existing.addresses,
      package: existing.package,
      pickupDropOff: existing.pickupDropOff,
      additionalCoverage: existing.additionalCoverage,
      signatureProtection: existing.signatureProtection,
      selectedService: selection,
      completedShipment: existing.completedShipment,
      completedSteps: existing.completedSteps,
      attestations: existing.attestations,
      coupon: existing.coupon,
      destinationResidential: existing.destinationResidential,
    });
  }

  /**
   * Returns persisted flow state or null when none exists.
   */
  load(): ShipmentFlowState | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }

    const raw = sessionStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as ShipmentFlowState;
    } catch {
      return null;
    }
  }

  /**
   * Clears all persisted shipment flow state.
   */
  clear(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Writes flow state to sessionStorage, binding the snapshot to the signed-in Okta subject.
   * Refuses to overwrite a snapshot owned by a different user.
   */
  private persist(state: ShipmentFlowState): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    const subject = this.auth?.getIdentitySubject?.()?.trim() || null;
    const ownerSubject = state.ownerSubject ?? this.readStoredOwnerSubject();
    if (subject && ownerSubject && ownerSubject !== subject) {
      this.clear();
      return;
    }

    const next: ShipmentFlowState = {
      ...state,
      ownerSubject: subject ?? ownerSubject,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }

  /**
   * Reads the owner subject already stored in sessionStorage without using {@link load},
   * so persist can compare identity before writing a new snapshot.
   */
  private readStoredOwnerSubject(): string | undefined {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return undefined;
      }

      const parsed = JSON.parse(raw) as ShipmentFlowState;
      return parsed.ownerSubject;
    } catch {
      return undefined;
    }
  }
}
